
const config = {
  importMethod: 'v3', // v1, v2, v3
}


function getImportV1(_locale) {
  // add more languages here
  // cannot set dynamic string inside import.meta.glob due to vite limitation
  function getImportMeta(_locale) {
    switch (_locale) {
      case 'en':
        return import.meta.glob('../assets/locales/en/*.json', { eager: true })
      case 'zhHant':
        return import.meta.glob('../assets/locales/zhHant/*.json', { eager: true })
      case 'ja':
        return import.meta.glob('../assets/locales/ja/*.json', { eager: true })
      default:
        throw new Error('Unknown locale ' + _locale)
    }
  }
  const req = getImportMeta(_locale)

  const translations = {}

  Object.keys(req).forEach((file) => {
    const name = file.split('/').pop().replace('.json', '')
    translations[name] = req[file]
  })
  return translations
}

function getImportV2(_locale) {
  switch (_locale) {
    case 'en':
      return import.meta.glob('../assets/locales/en.json', { eager: true })
    case 'zhHant':
      return import.meta.glob('../assets/locales/zhHant.json', { eager: true })
    case 'ja':
      return import.meta.glob('../assets/locales/ja.json', { eager: true })
    default:
      throw new Error('Unknown locale ' + _locale)
  }
}

async function getImportV3(_locale) {
  try {
    const res = await $this.$api.postData({
      data: {
        getFrontendLang: {
          api: 'AuthService.getFrontendLang',
          input: {
            locale: _locale
          }
        }
      }
    }, false);
    return res.getFrontendLang.output || {}
  }
  catch (err) {
    console.error('Failed to load translations from API:', err);
    return {};
  }
}


export async function loadTrans(_locale) {
  let translations = {}

  if (config.importMethod === 'v1') {
    translations = { ...translations, ...getImportV1(_locale) }
  }

  else if (config.importMethod === 'v2') {
    const importV2 = getImportV2(_locale)
    if (Object.keys(importV2).length === 1) {
      translations = { ...translations, ...importV2[Object.keys(importV2)[0]] }
    }
  }

  else if (config.importMethod === 'v3') {
    const importV3 = await getImportV3(_locale)
    translations = { ...translations, ...importV3 }
  }
  else {
    throw new Error('Unknown import method ' + config.importMethod)
  }

  translations = { ...translations, ...importV3 }

  // Translation Files From Other Paths
  const others = {
  }

  translations = Object.assign(translations, others)
  return JSON.parse(JSON.stringify(translations))
}

/**
 * Convert locale identifier to specified format
 * @param {String} locale The locale language code (any format that is in defined {@link vuetify const})
 * @param {String} to The target locale format to convert to
 * - Available values: **['vuetify', 'vee', 'bcp47']**
 * @return {String} The converted locale language code
 */
export function convertLocale(locale, to) {
  // Available locales in different formats
  /**
   * Vuetify Language Code, also rmb add import in src\config\vuetify.js
   * @see https://v2.vuetifyjs.com/en/features/internationalization/#getting-started
   */
  const vuetify = ['en', 'zhHans', 'zhHant', 'ja']
  // src\plugins\validations.js
  const vee = ['en', 'zh_CN', 'zh_TW', 'ja']
  /**
   * BCP47 Language Tags
   * must follow this below format, cause it is the standard format for language tags
   * @see https://www.iana.org/assignments/language-subtag-registry/language-subtag-registry
   * better UI:
   * @see https://www.techonthenet.com/js/language_tags.php
   */
  const bcp47 = ['en-US', 'zh-CN', 'zh-HK', 'ja-JP']
  const vuefilepond = ['en-en', 'zh-cn', 'zh-tw', 'ja-ja']
  const momentLocale = ['en', 'zh-cn', 'zh-hk', 'ja']

  /**
   * Selects the locale pack (defined {@link vuetify const}) to use
   * @param {String} to Target locale format / pack to choose
   * - Available values: **['vuetify', 'vee', 'bcp47']**
   * @return {Array<String> | null} Defined {@link vuetify const} language code | `null`: No match is found
   */
  function selectLocalePack(to) {
    if (to === 'vuetify') return vuetify
    if (to === 'vee') return vee
    if (to === 'bcp47') return bcp47
    if (to === 'vuefilepond') return vuefilepond
    if (to === 'momentLocale') return momentLocale
    return null
  }

  /**
   * Match / Maps / Translate locale identifier
   * @param {String} locale Source locale languge code format to match
   * @param {Array<String>} from Source locale language code definition ({@link vuetify const}) to match with input
   * @param {Array<String>} to Target locale language code to map to
   * @return {String | null} Converted locale language code | `null`: No match
   */
  function matchLocale(locale, from, to) {
    for (let i = 0; i < from.length; i++) {
      if (from[i] === locale) {
        return to[i]
      }
    }
    return null
  }

  const toArr = selectLocalePack(to)

  if (toArr !== null) {
    let result = null

    result = matchLocale(locale, vuetify, toArr)
    if (result) return result

    result = matchLocale(locale, vee, toArr)
    if (result) return result

    result = matchLocale(locale, bcp47, toArr)
    if (result) return result

    result = matchLocale(locale, vuefilepond, toArr)
    if (result) return result

    result = matchLocale(locale, momentLocale, toArr)
    if (result) return result
  }

  return locale
}

import moment from 'moment';
// need to import all related locales to make dynamic import work
import "moment/dist/locale/ja.js"
import "moment/dist/locale/zh-hk.js"
import { setLocale } from '@vee-validate/i18n';

export function useUpdateLocale(locale){
  window.vue.$vuetify.locale.current.value = convertLocale(locale, 'vuetify')

  moment.locale(convertLocale(locale, 'momentLocale'));

  setLocale(convertLocale(locale, 'vee'));
}
